/**
 * 2.5D 等距投影。
 *
 * 约定：格坐标 (tx, ty) 映射到该格菱形的**上顶点**。
 * 因此格 (tx,ty) 的四个角分别是 (tx,ty)、(tx+1,ty)、(tx+1,ty+1)、(tx,ty+1)，
 * 相邻格严丝合缝，不会出现缝隙或重叠。
 *
 * 这个映射是线性变换，意味着可以用一张 96×96 的纹理 + 一个变换矩阵
 * 直接铺满整张地图（战争迷雾与矿脉图层就是这么做的），无需逐格绘制。
 */

import { MAP_W, MAP_H } from '../shared/constants.ts';

export const TILE_W = 64;
export const TILE_H = 32;
export const HALF_W = TILE_W / 2;
export const HALF_H = TILE_H / 2;

/** 格坐标（可含小数）→ 世界像素 */
export function tileToWorldX(tx: number, ty: number): number {
  return (tx - ty) * HALF_W;
}

export function tileToWorldY(tx: number, ty: number): number {
  return (tx + ty) * HALF_H;
}

/** 世界像素 → 格坐标（可含小数），tileToWorld 的逆变换 */
export function worldToTileX(wx: number, wy: number): number {
  return wy / TILE_H + wx / TILE_W;
}

export function worldToTileY(wx: number, wy: number): number {
  return wy / TILE_H - wx / TILE_W;
}

/** 地图在世界坐标中的包围盒 */
export const WORLD_MIN_X = -MAP_H * HALF_W;
export const WORLD_MAX_X = MAP_W * HALF_W;
export const WORLD_MIN_Y = 0;
export const WORLD_MAX_Y = (MAP_W + MAP_H) * HALF_H;
export const WORLD_W = WORLD_MAX_X - WORLD_MIN_X;
export const WORLD_H = WORLD_MAX_Y - WORLD_MIN_Y;

/**
 * 把 96×96 的图层纹理铺到等距地图上的仿射矩阵参数。
 * Pixi 的 Matrix(a, b, c, d, tx, ty) 语义：x' = a·u + c·v + tx，y' = b·u + d·v + ty
 */
export const LAYER_MATRIX = {
  a: HALF_W,
  b: HALF_H,
  c: -HALF_W,
  d: HALF_H,
  tx: 0,
  ty: 0,
} as const;
